// ./src/components/features/EventTickets/TicketSalesFilterBar/TicketSalesFilterBar.tsx
//
// Filter controls for the issued-tickets / door-sales list: ticket type (multi),
// lifecycle state (multi), sale channel (multi). Controlled — the page maps it to
// an EventTicketFilter.

import React from "react";
import { Group, Button, Box } from "@mantine/core";
import { useTranslation } from "react-i18next";

import {
  TICKET_STATE_VALUES,
  TICKET_STATE_LABELS,
  TICKET_SALE_CHANNEL_VALUES,
  TICKET_SALE_CHANNEL_LABELS,
} from "@/utils/api";
import { AppIcon, MultiSelectField } from "@/components/generic";

export interface TicketTypeOption {
  value: string;
  label: string;
}

export interface TicketSalesFilterValue {
  /** ROADMAP A4 — the dates of a recurring event the tickets are for. */
  dateIds: string[];
  ticketTypeIds: string[];
  ticketStates: string[];
  saleChannels: string[];
  /** #169 — scanned in (an add-on: handed out) at one of these service points. */
  pointIds: string[];
}

export interface TicketSalesFilterBarProps {
  value: TicketSalesFilterValue;
  onChange: (value: TicketSalesFilterValue) => void;
  /** Ticket-type options for this event (General Admission, VIP, …). */
  typeOptions?: TicketTypeOption[];
  /** ROADMAP A4 — the event's dates; the filter only shows on a recurring event that has some. */
  dateOptions?: TicketTypeOption[];
  /** #169 — the event's service points; the filter only shows when it has some. */
  pointOptions?: TicketTypeOption[];
  disabled?: boolean;
  showClear?: boolean;
}

export const EMPTY_TICKET_SALES_FILTER: TicketSalesFilterValue = {
  dateIds: [],
  ticketTypeIds: [],
  ticketStates: [],
  saleChannels: [],
  pointIds: [],
};

export const isTicketSalesFilterActive = (v: TicketSalesFilterValue): boolean =>
  v.dateIds.length > 0 || v.ticketTypeIds.length > 0 || v.ticketStates.length > 0 || v.saleChannels.length > 0 || v.pointIds.length > 0;

const TYPE_STYLE: React.CSSProperties = { flex: "1 1 240px", minWidth: 220 };
const STATE_STYLE: React.CSSProperties = { flex: "1 1 240px", minWidth: 220 };
const CHANNEL_STYLE: React.CSSProperties = { flex: "1 1 220px", minWidth: 200 };

const TicketSalesFilterBarComponent: React.FC<TicketSalesFilterBarProps> = ({
  value,
  onChange,
  typeOptions = [],
  dateOptions = [],
  pointOptions = [],
  disabled = false,
  showClear = true,
}) => {
  const { t } = useTranslation(["ticketSales", "common"]);

  const stateOptions = TICKET_STATE_VALUES.map((v) => ({
    value: v,
    label: t(`ticketSales:state.${v}`, { defaultValue: TICKET_STATE_LABELS[v] }),
  }));

  const channelOptions = TICKET_SALE_CHANNEL_VALUES.map((v) => ({
    value: v,
    label: t(`ticketSales:channel.${v}`, { defaultValue: TICKET_SALE_CHANNEL_LABELS[v] }),
  }));

  const active = isTicketSalesFilterActive(value);

  return (
    <Group gap="sm" align="flex-end" wrap="wrap">
      {dateOptions.length > 0 && (
        <Box style={TYPE_STYLE}>
          <MultiSelectField
            label={t("ticketSales:filters.date.label", { defaultValue: "Date" })}
            placeholder={value.dateIds.length > 0 ? undefined : t("ticketSales:filters.date.placeholder", { defaultValue: "Any date" })}
            data={dateOptions}
            value={value.dateIds}
            onChange={(_name, next) => onChange({ ...value, dateIds: next })}
            disabled={disabled}
            clearable
            searchable
            size="md"
          />
        </Box>
      )}

      {typeOptions.length > 0 && (
        <Box style={TYPE_STYLE}>
          <MultiSelectField
            label={t("ticketSales:filters.type.label", { defaultValue: "Ticket type" })}
            placeholder={value.ticketTypeIds.length > 0 ? undefined : t("ticketSales:filters.type.placeholder", { defaultValue: "Any type" })}
            data={typeOptions}
            value={value.ticketTypeIds}
            onChange={(_name, next) => onChange({ ...value, ticketTypeIds: next })}
            disabled={disabled}
            clearable
            searchable
            size="md"
          />
        </Box>
      )}

      <Box style={STATE_STYLE}>
        <MultiSelectField
          label={t("ticketSales:filters.state.label", { defaultValue: "Ticket status" })}
          placeholder={value.ticketStates.length > 0 ? undefined : t("ticketSales:filters.state.placeholder", { defaultValue: "Any status" })}
          data={stateOptions}
          value={value.ticketStates}
          onChange={(_name, next) => onChange({ ...value, ticketStates: next })}
          disabled={disabled}
          clearable
          searchable
          size="md"
        />
      </Box>

      <Box style={CHANNEL_STYLE}>
        <MultiSelectField
          label={t("ticketSales:filters.channel.label", { defaultValue: "Sold via" })}
          placeholder={value.saleChannels.length > 0 ? undefined : t("ticketSales:filters.channel.placeholder", { defaultValue: "Any channel" })}
          data={channelOptions}
          value={value.saleChannels}
          onChange={(_name, next) => onChange({ ...value, saleChannels: next })}
          disabled={disabled}
          clearable
          searchable
          size="md"
        />
      </Box>

      {pointOptions.length > 0 && (
        <Box style={CHANNEL_STYLE}>
          <MultiSelectField
            label={t("ticketSales:filters.point.label", { defaultValue: "Scanned at" })}
            placeholder={value.pointIds.length > 0 ? undefined : t("ticketSales:filters.point.placeholder", { defaultValue: "Anywhere" })}
            data={pointOptions}
            value={value.pointIds}
            onChange={(_name, next) => onChange({ ...value, pointIds: next })}
            disabled={disabled}
            clearable
            searchable
            size="md"
          />
        </Box>
      )}

      {active && showClear && (
        <Button
          variant="subtle"
          color="gray"
          size="md"
          onClick={() => onChange(EMPTY_TICKET_SALES_FILTER)}
          disabled={disabled}
          leftSection={<AppIcon iconName="X" size={16} />}
        >
          {t("common:buttons.clear", { defaultValue: "Clear" })}
        </Button>
      )}
    </Group>
  );
};

export const TicketSalesFilterBar = React.memo(TicketSalesFilterBarComponent);
